import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { FaEye, FaFileInvoiceDollar } from "react-icons/fa";
import { toast } from "react-toastify";
import Grid from "../components/Grid";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { FaDownload } from "react-icons/fa";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function GoodReceiptNote() {
  const [grnList, setGRNList] = useState([]);
  const [selectedGRN, setSelectedGRN] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const inputRefs = useRef([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const navigate = useNavigate();
  const [grnInvoiceDetails, setGRNInvoiceDetails] = useState([]);
  const [isViewingInvoice, setIsViewingInvoice] = useState(false);
  const [selectedSearchField, setSelectedSearchField] = useState("grn_code");
  const [invoiceQuantities, setInvoiceQuantities] = useState({});
  const [remainingQuantities, setRemainingQuantities] = useState({}); // NEW STATE

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("No auth token found.");
      return;
    }

    try {
      const res = await axios.get(`${API_BASE_URL}/grn/getAllGRNs`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const formatted = res.data.map((grn) => {
        const items =
          grn.items?.map((item, index) => ({
            id: index,
            product_id: item.product?._id,
            product_name: item.product?.product_name || "N/A",
            quantity_received: item.quantity_received || 0,
            unit_price: item.unit_price || 0,
            total_price: item.total_price || 0,
            invoiced: item.invoiced || false,
            invoice_code: item.invoice_code || "",
          })) || [];

        const total_amount = items
          .reduce((sum, item) => sum + (item.total_price || 0), 0)
          .toFixed(2);

        return {
          id: grn._id,
          grn_code: grn.grn_code,
          grn_date: new Date(grn.grn_date).toLocaleDateString("en-US"),
          supplier_name:
            grn.purchase_order?.supplier?.supplier_name ||
            grn.dispatched_stock_on_loan_id?.organization?.organization_name ||
            "N/A",
          total_amount,
          status: grn.status || "N/A",
          purchase_order_code:
            grn.purchase_order?.purchase_order_code ||
            grn.dispatched_stock_on_loan_id?.dispatched_stock_on_loan_code ||
            "N/A",
          location_name: grn.location?.location_name || "N/A",
          has_invoice: grn.invoice_created || false,
          items,
          created_by: grn.created_by || null,
        };
      });

      for (let grn of formatted) {
        try {
          const res = await axios.get(
            `${API_BASE_URL}/grnInvoices/by-grn/${grn.id}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );

          if (res.data.invoices?.length > 0) {
            const invoiceTotal = res.data.invoices.reduce(
              (sum, inv) => sum + (inv.amount || 0),
              0
            );
            grn.total_amount = invoiceTotal.toFixed(2);
          }
        } catch (err) {
          console.warn(`No invoices found for GRN ${grn.grn_code}`);
        }
      }

      setGRNList(formatted);
    } catch (err) {
      toast.error("Failed to fetch GRNs.");
    }
  };

  // NEW FUNCTION: Fetch remaining quantities from backend
  const fetchRemainingQuantities = async (grnId) => {
    const token = localStorage.getItem("token");
    if (!token) return;

    try {
      const res = await axios.get(
        `${API_BASE_URL}/grnInvoices/grn/${grnId}/remaining`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Convert array to object for easy lookup
      const remainingMap = {};
      res.data.remaining.forEach((item) => {
        remainingMap[item.product_id] = {
          received_quantity: item.received_quantity,
          invoiced_quantity: item.invoiced_quantity,
          remaining_quantity: item.remaining_quantity,
          invoice_codes: item.invoice_codes,
        };
      });

      setRemainingQuantities(remainingMap);
      return remainingMap;
    } catch (error) {
      console.error("Failed to fetch remaining quantities:", error);
      toast.error("Failed to fetch remaining quantities.");
      return {};
    }
  };

  const handleUnitPriceChange = (index, value) => {
    setSelectedGRN((prev) => {
      const newItems = [...prev.items];
      newItems[index] = {
        ...newItems[index],
        unit_price: value === "" ? 0 : parseFloat(value) || 0,
      };
      return { ...prev, items: newItems };
    });
  };

  const handleUnitPriceEnter = (index) => {
    setSelectedGRN((prev) => {
      const newItems = [...prev.items];
      const item = newItems[index];
      const qty = Number(item.quantity_received) || 0;
      const price = Number(item.unit_price) || 0;

      newItems[index] = {
        ...item,
        total_price: qty * price,
      };

      const totalAmount = newItems
        .reduce((sum, i) => sum + (i.total_price || 0), 0)
        .toFixed(2);

      return {
        ...prev,
        items: newItems,
        total_amount: totalAmount,
      };
    });

    setTimeout(() => {
      if (inputRefs.current[index + 1]) {
        inputRefs.current[index + 1].focus();
      }
    }, 100);
  };

  const handleSavePrices = async () => {
    if (selectedGRN.invoiced) {
      toast.info("Invoice already created. Cannot change prices.");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("No auth token found.");
      return;
    }

    try {
      const updated_items = selectedGRN.items.map((item) => ({
        product_id: item.product_id,
        new_unit_price: item.unit_price,
      }));

      await axios.put(
        `${API_BASE_URL}/grn/update-grn-prices`,
        {
          grn_id: selectedGRN.id,
          updated_items,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setSelectedGRN(null);
      fetchData();
    } catch (error) {
      console.error(error);
      toast.error("Failed to update prices.");
    }
  };

  const handleCreateInvoice = async () => {
    if (!selectedGRN || selectedProducts.length === 0) {
      toast.error("Please select one or more products to invoice.");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Authentication token is missing.");
      return;
    }

    const product_items = selectedGRN.items
      .filter((item) => selectedProducts.includes(item.product_id))
      .map((item) => {
        const remainingData = remainingQuantities[item.product_id];
        const maxQty = remainingData
          ? remainingData.remaining_quantity
          : item.quantity_received;
        const invoiceQty = invoiceQuantities[item.product_id] || maxQty;

        return {
          product_id: item.product_id,
          product_name: item.product_name,
          quantity: invoiceQty > 0 ? invoiceQty : maxQty,
          unit_price: item.unit_price,
        };
      })
      .filter((item) => item.quantity > 0);

    const invalidItems = product_items.filter(
      (item) => !item.unit_price || item.unit_price <= 0
    );

    if (invalidItems.length > 0) {
      const names = invalidItems.map((i) => i.product_name).join(", ");
      toast.error(`Please enter unit prices for: ${names}`);
      return;
    }

    const amount = product_items.reduce(
      (sum, item) => sum + item.unit_price * item.quantity,
      0
    );

    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + 14);

    const invoiceData = {
      grn_id: selectedGRN.id,
      product_items,
      purchase_order_code: selectedGRN.purchase_order_code,
      location_name: selectedGRN.location_name,
      supplier_name: selectedGRN.supplier_name,
      amount,
      balance: amount,
      due_date: dueDate.toISOString().split("T")[0],
    };

    try {
      const res = await axios.post(
        `${API_BASE_URL}/grnInvoices/create`,
        invoiceData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success("Invoice created successfully!");
      navigate(`/grnInvoices/get/${res.data.invoice._id}`);
      setSelectedGRN(null);
      setSelectedProducts([]);
      setRemainingQuantities({});
      setInvoiceQuantities({});
      fetchData();
    } catch (error) {
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to create invoice.");
      }
    }
  };

  const handleInvoiceIconClick = async (row) => {
    const fullGRN = grnList.find((grn) => grn.grn_code === row.grn_code);
    if (!fullGRN) return;

    if (fullGRN.has_invoice) {
      const token = localStorage.getItem("token");
      try {
        const res = await axios.get(
          `${API_BASE_URL}/grnInvoices/by-grn/${fullGRN.id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        setGRNInvoiceDetails(res.data.invoices || []);
        setIsViewingInvoice(true);
        setSelectedGRN(fullGRN);

        // Fetch remaining quantities
        await fetchRemainingQuantities(fullGRN.id);
      } catch (error) {
        toast.error("Failed to fetch invoice details.");
      }
    } else {
      setIsViewingInvoice(false);
      setSelectedGRN(fullGRN);

      // Fetch remaining quantities
      await fetchRemainingQuantities(fullGRN.id);
    }
  };

  const filteredGRNs = grnList.filter((grn) => {
    if (!searchTerm) return true;
    const field = selectedSearchField;
    let fieldValue = grn[field];
    if (!fieldValue) fieldValue = "";
    return fieldValue.toString().toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleDownloadExcel = async () => {
    if (filteredGRNs.length === 0) {
      toast.warn("No GRN data to export.");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Good Receipt");

    const headerRow = [
      "GRN Code",
      "Source Code",
      "Supplier",
      "Location",
      "Total Amount (LKR)",
      "GRN Date",
      "Status",
    ];
    worksheet.addRow(headerRow);

    const header = worksheet.getRow(1);
    header.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD966" },
      };
      cell.font = { bold: true };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    filteredGRNs.forEach((grn) => {
      worksheet.addRow([
        grn.grn_code,
        grn.purchase_order_code,
        grn.supplier_name,
        grn.location_name,
        Number(grn.total_amount).toFixed(2),
        grn.grn_date,
        grn.status,
      ]);
    });

    worksheet.columns.forEach((column, i) => {
      let maxLength = headerRow[i].length;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const cellLength = cell.value ? cell.value.toString().length : 0;
        if (cellLength > maxLength) maxLength = cellLength;
      });
      column.width = maxLength < 20 ? 20 : maxLength;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "good_receipt_notes.xlsx");
  };

  const mainColumns = [
    { field: "grn_code", headerName: "GRN Code", flex: 1 },
    { field: "grn_date", headerName: "GRN Date", flex: 1 },
    { field: "purchase_order_code", headerName: "Source Code", flex: 1 },
    { field: "supplier_name", headerName: "Supplier", flex: 1 },
    { field: "location_name", headerName: "Location", flex: 1 },
    { field: "total_amount", headerName: "Total Amount", flex: 1 },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const status = params.row.status;
        const statusClassMap = {
          Drafted: "bg-gray-400 text-white",
          "In-Progress": "bg-blue-400 text-white",
          Completed: "bg-green-400 text-white",
        };
        const statusClass = statusClassMap[status] || "bg-gray-500 text-white";
        return (
          <span className={`px-2 py-1 rounded-sm ${statusClass}`}>
            {status}
          </span>
        );
      },
    },
    {
      field: "created_by",
      headerName: "Created By",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <span>{params.row.created_by?.username || "N/A"}</span>
      ),
    },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <div className="flex gap-2 justify-center w-full">
          <button
            onClick={async () => {
              const fullGRN = grnList.find(
                (grn) => grn.grn_code === params.row.grn_code
              );

              if (!fullGRN) return;

              setSelectedGRN(fullGRN);
              setSelectedProducts([]);
              setInvoiceQuantities({});

              // Fetch remaining quantities
              await fetchRemainingQuantities(fullGRN.id);

              if (fullGRN.has_invoice) {
                const token = localStorage.getItem("token");
                try {
                  const res = await axios.get(
                    `${API_BASE_URL}/grnInvoices/by-grn/${fullGRN.id}`,
                    {
                      headers: { Authorization: `Bearer ${token}` },
                    }
                  );
                  setGRNInvoiceDetails(res.data.invoices || []);
                } catch (err) {
                  toast.error("Failed to fetch invoice details.");
                }
              } else {
                setGRNInvoiceDetails([]);
              }

              setIsViewingInvoice(false);
            }}
            className="bg-[#FBC02D] text-white p-2 rounded"
            title="View GRN Details"
          >
            <FaEye />
          </button>

          <button
            onClick={() => handleInvoiceIconClick(params.row)}
            className="bg-[#4CAF50] text-white p-2 flex items-center justify-center rounded"
          >
            <FaFileInvoiceDollar className="text-white" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-4">
      <h1 className="text-3xl font-bold text-green-700 mb-6">
        Good Receipt
      </h1>

      <div className="bg-white p-4 rounded shadow">
        <div className="flex justify-between mb-4 items-center flex-wrap gap-4">
          <div className="flex items-center flex-wrap gap-4">
            <select
              className="px-4 py-2 border border-gray-300 rounded-lg"
              value={selectedSearchField}
              onChange={(e) => setSelectedSearchField(e.target.value)}
            >
              <option value="grn_code">GRN Code</option>
              <option value="purchase_order_code">Source Code</option>
              <option value="supplier_name">Supplier</option>
              <option value="location_name">Location</option>
              <option value="grn_date">GRN Date</option>
              <option value="status">Status</option>
            </select>

            <div className="relative">
              <input
                type="text"
                placeholder="Search GRNs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-4 py-2 pl-10 border border-gray-300 rounded-lg w-full max-w-xs"
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                <FontAwesomeIcon icon={faSearch} className="text-gray-500" />
              </div>
            </div>
          </div>

          <div>
            <button
              onClick={handleDownloadExcel}
              className="flex items-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
            >
              <FaDownload className="mr-2" />
              Download Excel
            </button>
          </div>
        </div>
        <Grid
          rows={filteredGRNs.sort((a, b) => {
            const numA = parseInt(a.grn_code.split("-").pop(), 10);
            const numB = parseInt(b.grn_code.split("-").pop(), 10);
            return numB - numA;
          })}
          columns={mainColumns}
          getRowId={(row) => row.grn_code}
        />
      </div>

      {selectedGRN && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-3xl relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => {
                setSelectedGRN(null);
                setSelectedProducts([]);
                setGRNInvoiceDetails(null);
                setIsViewingInvoice(false);
                setRemainingQuantities({});
                setInvoiceQuantities({});
              }}
              className="absolute top-2 right-2 bg-red-600 text-white square-full w-8 h-8 text-xl font-bold flex items-center justify-center cursor-pointer"
              title="Close"
            >
              &times;
            </button>

            <h2 className="text-xl font-bold mb-4 text-center">
              {isViewingInvoice ? "GRN Invoice Details" : "GRN Details"}
            </h2>

            <div className="grid grid-cols-2 gap-4 text-sm mb-4">
              <div>
                <strong>GRN Code:</strong> {selectedGRN.grn_code}
              </div>
              <div>
                <strong>Source Code:</strong> {selectedGRN.purchase_order_code}
              </div>
              <div>
                <strong>Supplier:</strong> {selectedGRN.supplier_name}
              </div>
              <div>
                <strong>Location:</strong> {selectedGRN.location_name}
              </div>
              <div>
                <strong>GRN Date:</strong> {selectedGRN.grn_date}
              </div>
            </div>

            {isViewingInvoice && grnInvoiceDetails.length > 0 ? (
              <>
                {grnInvoiceDetails.map((invoice, idx) => (
                  <div key={invoice._id} className="mb-6 border-t pt-4">
                    <h3 className="font-semibold text-lg mb-2">
                      Invoice #{idx + 1} -{" "}
                      <span className="text-blue-600">
                        {invoice.invoice_code}
                      </span>
                    </h3>

                    <DataGrid
                      autoHeight
                      rows={invoice.product_items.map((item, index) => ({
                        id: index,
                        ...item,
                        unit_name: item.unit?.unit_name || "N/A",
                      }))}
                      columns={[
                        {
                          field: "product_name",
                          headerName: "Product",
                          flex: 2,
                        },
                        { field: "quantity", headerName: "Qty", flex: 1 },
                        { field: "unit_name", headerName: "Unit", flex: 1 },
                        {
                          field: "unit_price",
                          headerName: "Unit Price (Rs.)",
                          flex: 1,
                          renderCell: (params) =>
                            `Rs. ${Number(params.row.unit_price).toFixed(2)}`,
                        },
                        {
                          field: "total",
                          headerName: "Total (Rs.)",
                          flex: 1,
                          renderCell: (params) =>
                            `Rs. ${(
                              params.row.unit_price * params.row.quantity
                            ).toFixed(2)}`,
                        },
                      ]}
                      pageSize={5}
                      rowsPerPageOptions={[5]}
                      disableSelectionOnClick
                      hideFooterSelectedRowCount
                    />

                    <div className="mt-2 text-right text-sm">
                      <div>
                        <strong>Total:</strong> Rs.{" "}
                        {Number(invoice.amount).toFixed(2)}
                      </div>
                      <div>
                        <strong>Balance:</strong> Rs.{" "}
                        {Number(invoice.balance).toFixed(2)}
                      </div>
                      <div>
                        <strong>Due Date:</strong>{" "}
                        {new Date(invoice.due_date).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                ))}
                {/* --- MODIFICATION START --- */}
                <div className="mt-6 pt-4 border-t-2 border-gray-400 text-right">
                  <strong className="text-lg">
                    Grand Total of All Invoices: Rs.{" "}
                    {grnInvoiceDetails
                      .reduce(
                        (sum, invoice) => sum + Number(invoice.amount),
                        0
                      )
                      .toFixed(2)}
                  </strong>
                </div>
                {/* --- MODIFICATION END --- */}
              </>
            ) : (
              <>
                <h3 className="font-semibold mt-4 mb-2">Items</h3>

                <DataGrid
                  autoHeight
                  rows={selectedGRN.items.map((item, index) => ({
                    ...item,
                    id: index,
                  }))}
                  columns={[
                    {
                      field: "select",
                      headerName: "Select",
                      width: 60,
                      renderCell: (params) => {
                        const productId = params.row.product_id;
                        const remainingData = remainingQuantities[productId];
                        const isFullyInvoiced =
                          remainingData &&
                          remainingData.remaining_quantity === 0;
                        const isChecked = selectedProducts.includes(productId);

                        return (
                          <input
                            type="checkbox"
                            disabled={isFullyInvoiced}
                            checked={isChecked}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedProducts((prev) => [
                                  ...prev,
                                  productId,
                                ]);
                              } else {
                                setSelectedProducts((prev) =>
                                  prev.filter((id) => id !== productId)
                                );
                              }
                            }}
                          />
                        );
                      },
                    },
                    {
                      field: "product_name",
                      headerName: "Product Name",
                      flex: 1,
                    },
                    {
                      field: "invoice_qty",
                      headerName: "Qty",
                      flex: 0.5,
                      renderCell: (params) => {
                        const productId = params.row.product_id;
                        const remainingData = remainingQuantities[productId];
                        const maxQty = remainingData
                          ? remainingData.remaining_quantity
                          : params.row.quantity_received;

                        const value =
                          invoiceQuantities[productId] !== undefined
                            ? invoiceQuantities[productId]
                            : maxQty;

                        return (
                          <input
                            type="number"
                            min={1}
                            max={maxQty}
                            value={value}
                            onChange={(e) => {
                              let val = Number(e.target.value);
                              if (val > maxQty) val = maxQty;
                              if (val < 1) val = 1;
                              setInvoiceQuantities((prev) => ({
                                ...prev,
                                [productId]: val,
                              }));
                            }}
                            className="border px-2 rounded w-full"
                            disabled={maxQty === 0}
                          />
                        );
                      },
                    },
                    {
                      field: "unit_price",
                      headerName: "Unit Price (Rs.)",
                      flex: 0.8,
                      renderCell: (params) => {
                        const productId = params.row.product_id;
                        const remainingData = remainingQuantities[productId];
                        const isFullyInvoiced =
                          remainingData &&
                          remainingData.remaining_quantity === 0;

                        const canEditPrice =
                          selectedGRN &&
                          selectedGRN.purchase_order_code?.includes("DSOL") &&
                          !isFullyInvoiced;

                        return (
                          <input
                            type="number"
                            min={0.01}
                            step="0.01"
                            className={`border px-2 rounded w-full ${
                              !canEditPrice
                                ? "bg-white cursor-not-allowed text-gray-600"
                                : ""
                            }`}
                            value={
                              params.row.unit_price > 0
                                ? params.row.unit_price
                                : ""
                            }
                            ref={(el) =>
                              (inputRefs.current[params.row.id] = el)
                            }
                            onChange={(e) => {
                              if (canEditPrice)
                                handleUnitPriceChange(
                                  params.row.id,
                                  e.target.value
                                );
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && canEditPrice) {
                                handleUnitPriceEnter(params.row.id);
                              }
                            }}
                            disabled={!canEditPrice}
                          />
                        );
                      },
                    },
                    {
                      field: "total_price",
                      headerName: "Total (Rs.)",
                      flex: 0.7,
                      renderCell: (params) => {
                        const productId = params.row.product_id;
                        const remainingData = remainingQuantities[productId];
                        const qty =
                          invoiceQuantities[productId] ||
                          (remainingData
                            ? remainingData.remaining_quantity
                            : params.row.quantity_received);

                        const unitPrice = Number(params.row.unit_price) || 0;
                        const total = qty * unitPrice;

                        return isNaN(total)
                          ? "Rs. 0.00"
                          : `Rs. ${total.toFixed(2)}`;
                      },
                    },
                    {
                      field: "invoice_code",
                      headerName: "Invoice No",
                      flex: 1,
                      headerAlign: "center",
                      align: "center",
                      renderCell: (params) => {
                        const productId = params.row.product_id;
                        const remainingData = remainingQuantities[productId];
                        const invoiceCodes =
                          remainingData?.invoice_codes || [];

                        return invoiceCodes.length > 0 ? (
                          <span className="text-green-600 font-semibold">
                            {invoiceCodes.join(", ")}
                          </span>
                        ) : (
                          <span className="text-gray-600 font-semibold">
                            -
                          </span>
                        );
                      },
                    },
                  ]}
                  pageSize={5}
                  rowsPerPageOptions={[5]}
                  disableSelectionOnClick
                  hideFooterSelectedRowCount
                />
                {selectedProducts.length > 0 &&
                  selectedGRN &&
                  selectedGRN.purchase_order_code && (
                    <button
                      onClick={() => {
                        handleSavePrices();
                        handleCreateInvoice();
                      }}
                      className="bg-green-600 text-white px-4 py-2 rounded mt-4 ml-4 float-left"
                    >
                      Create Invoice
                    </button>
                  )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default GoodReceiptNote;